import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";

interface LayerMappingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detectedLayers: string[];
  currentMappings: Record<string, string>;
  onApply: (mappings: Record<string, string>, saveAsDefault: boolean) => void;
}

const CATEGORY_OPTIONS = [
  { value: 'facet', label: 'Roof Facet' },
  { value: 'eave', label: 'Eave' },
  { value: 'rake', label: 'Rake' },
  { value: 'ridge', label: 'Ridge' },
  { value: 'hip', label: 'Hip' },
  { value: 'valley', label: 'Valley' },
  { value: 'wallStepFlashing', label: 'Wall/Step Flashing' },
  { value: 'dripEdge', label: 'Drip Edge' },
  { value: 'pipe', label: 'Pipe/Vent' },
  { value: 'skylight', label: 'Skylight' },
  { value: 'chimney', label: 'Chimney' },
  { value: 'fan', label: 'Fan/Vent' },
  { value: 'ignore', label: 'Ignore (Don\'t Import)' },
];

export const LayerMappingDialog = ({
  open,
  onOpenChange,
  detectedLayers,
  currentMappings,
  onApply
}: LayerMappingDialogProps) => {
  const [mappings, setMappings] = useState<Record<string, string>>(currentMappings);
  const [saveAsDefault, setSaveAsDefault] = useState(false);

  const handleMappingChange = (layer: string, category: string) => {
    setMappings(prev => ({
      ...prev,
      [layer]: category
    }));
  };

  const handleApply = () => {
    onApply(mappings, saveAsDefault);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Adjust Layer Mapping</DialogTitle>
          <DialogDescription>
            Map each DXF layer to the correct category for accurate measurements.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-4">
            {detectedLayers.map((layer) => (
              <div key={layer} className="grid grid-cols-2 gap-4 items-center">
                <Label className="font-mono text-sm">{layer}</Label>
                <Select
                  value={mappings[layer] || 'ignore'}
                  onValueChange={(value) => handleMappingChange(layer, value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="saveDefault"
            checked={saveAsDefault}
            onCheckedChange={(checked) => setSaveAsDefault(checked as boolean)}
          />
          <Label
            htmlFor="saveDefault"
            className="text-sm font-normal cursor-pointer"
          >
            Save as default mapping for future uploads
          </Label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply}>
            Apply & Re-parse
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
